import { useState, useEffect, useCallback } from 'react'
import { lumi } from '@/lib/lumi'

/**
 * Hook que calcula el número de notificaciones publicadas sin leer
 * para una audiencia (empresas | colaboradores) y un usuario concreto.
 *
 * - Refresca automáticamente cada 30s.
 * - Escucha el evento global 'beeznz:notifications-changed' para refrescar al instante
 *   cuando una notificación se marca como leída desde la página de notificaciones.
 */
export function useUnreadNotifications(
  audience: 'empresas' | 'colaboradores',
  userId?: string,
) {
  const [unread, setUnread] = useState(0)

  const refresh = useCallback(async () => {
    if (!userId) {
      setUnread(0)
      return
    }
    try {
      const res = await lumi.entities.notifications.list({
        filter: {
          status: 'publicada',
          $or: [
            { targetAudience: audience },
            { targetAudience: 'todos' },
            { targetUserId: userId },
          ],
        },
        limit: 200,
      })
      const count = (res.list as Array<{ readBy?: string[] }>).filter(
        (n) => !(n.readBy ?? []).includes(userId),
      ).length
      setUnread(count)
    } catch {
      // Silencioso: no rompemos la UI por un fallo de red transitorio
    }
  }, [audience, userId])

  useEffect(() => {
    refresh()
    const interval = setInterval(refresh, 30000)
    const handler = () => refresh()
    window.addEventListener('beeznz:notifications-changed', handler)
    return () => {
      clearInterval(interval)
      window.removeEventListener('beeznz:notifications-changed', handler)
    }
  }, [refresh])

  return { unread, refresh }
}

/** Dispara el refresco del badge de notificaciones en toda la app */
export function notifyNotificationsChanged() {
  window.dispatchEvent(new Event('beeznz:notifications-changed'))
}
